import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useRecentItems } from '../hooks/useRecentItems.js';

/**
 * Cmd-K command palette (card spotlight-067).
 *
 * Behaviour
 *   - Cmd-K (Mac) / Ctrl-K (other) opens from anywhere.
 *   - Esc dismisses; click-on-backdrop dismisses.
 *   - Empty query renders the recently-visited list (sessionStorage).
 *   - Typing fans out to ``GET /v1/admin/search`` with a 150 ms
 *     debounce — short enough to feel snappy for the "type three
 *     letters, hit enter" muscle memory the card calls out as
 *     within 200 ms; long enough to coalesce a "spo" burst into
 *     one request.
 *   - Up/Down moves the active row; Enter navigates.
 *
 * Shape
 *   - Fullscreen overlay rendered into a stacking context with
 *     z-index well above HeaderBar (we use 100).
 *   - Single flat scrollable list; group headers separate kinds.
 *   - Each row carries enough copy (label, kind glyph, sub-line) to
 *     pick the right hit on a glance.
 */

const KIND_LABEL = {
  project: 'Projects',
  tour: 'Tours',
  content: 'Content',
  member: 'Members',
  api_key: 'API keys',
  site: 'Sites',
  help_thread: 'Help threads',
  recent: 'Recently visited',
};

const KIND_GLYPH = {
  project: 'P',
  tour: 'T',
  content: 'C',
  member: 'M',
  api_key: 'K',
  site: 'S',
  help_thread: 'H',
  recent: '*',
};

const ORDER = ['project', 'tour', 'content', 'member', 'api_key', 'site', 'help_thread'];

function isCmdOrCtrlK(e) {
  if (e.key !== 'k' && e.key !== 'K') return false;
  return e.metaKey || e.ctrlKey;
}

/**
 * Controlled mount — Shell renders <CommandPalette /> once and we
 * own the open/closed state internally so the hotkey handler stays
 * here.  ``initialQuery`` is set by the sidebar's pinned search
 * input when the user starts typing there before opening the
 * overlay.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const [recent, pushRecent] = useRecentItems();

  // ── Hotkey + custom-event opener ───────────────────────────────
  // The sidebar input dispatches a ``spotlight:cmdk-open`` CustomEvent
  // with the in-progress query so the palette opens pre-filled —
  // saves the user retyping what they already typed in the rail.
  useEffect(() => {
    function onKeyDown(e) {
      if (isCmdOrCtrlK(e)) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    function onOpenEvent(e) {
      const q = (e && e.detail && e.detail.query) || '';
      setQuery(q);
      setOpen(true);
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('spotlight:cmdk-open', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('spotlight:cmdk-open', onOpenEvent);
    };
  }, [open]);

  // Focus the input on every open; reset query when the user
  // dismisses so the next invocation is a clean slate.
  useEffect(() => {
    if (open) {
      // requestAnimationFrame so the input element exists before
      // focus() fires (React mounts the overlay this tick).
      requestAnimationFrame(() => inputRef.current?.focus());
      setActive(0);
    } else {
      setQuery('');
      setGroups([]);
      setError(null);
    }
  }, [open]);

  // ── Debounced fetch ────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (!trimmed) {
      setGroups([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const resp = await api(`/v1/admin/search?q=${encodeURIComponent(trimmed)}`);
        if (!alive) return;
        // Stale-response guard — drop a hit that overtakes a newer
        // keystroke.  The server echoes the query it served so we
        // only commit when it matches what's in the box now.
        if ((resp?.query || '').trim() !== trimmed) return;
        setGroups(resp?.groups || []);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Search failed');
        setGroups([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 150);
    return () => { alive = false; clearTimeout(t); };
  }, [query, open, api]);

  // Flatten groups into a single index-addressable list for arrow
  // navigation.  Empty query falls back to the recents list — each
  // recent is rendered under a single synthetic ``recent`` group.
  const flat = useMemo(() => {
    const out = [];
    const trimmed = query.trim();
    if (!trimmed) {
      if (recent.length > 0) {
        out.push({ kind: 'recent', heading: KIND_LABEL.recent });
        for (const item of recent) {
          out.push({ kind: 'recent', item, glyph: KIND_GLYPH[item.kind] || '*' });
        }
      }
      return out;
    }
    for (const k of ORDER) {
      const group = groups.find((g) => g.kind === k);
      if (!group || !group.items?.length) continue;
      out.push({ kind: k, heading: KIND_LABEL[k] || k });
      for (const item of group.items) {
        out.push({ kind: k, item, glyph: KIND_GLYPH[k] || '?' });
      }
    }
    return out;
  }, [query, groups, recent]);

  // Indexes of rows that are pickable (not headings) — Up/Down skips
  // headings, which would otherwise leave the user stuck on a label.
  const pickableIdx = useMemo(
    () => flat.map((row, i) => (row.item ? i : -1)).filter((i) => i >= 0),
    [flat],
  );

  // Keep ``active`` clamped to a real row when the result set changes.
  useEffect(() => {
    if (pickableIdx.length === 0) { setActive(-1); return; }
    if (!pickableIdx.includes(active)) setActive(pickableIdx[0]);
  }, [pickableIdx, active]);

  const pick = useCallback((row) => {
    if (!row || !row.item) return;
    const target = row.item;
    // Push into recents under the row's resolved kind so the next
    // empty-query open surfaces it with the right glyph.  When the
    // row is itself a recent, we keep the original kind.
    const kindForRecent = row.kind === 'recent' ? (target.kind || 'recent') : row.kind;
    pushRecent({
      ...target,
      kind: kindForRecent,
    });
    setOpen(false);
    if (target.url) navigate(target.url);
  }, [navigate, pushRecent]);

  function onKeyDownInput(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const i = pickableIdx.indexOf(active);
      const next = pickableIdx[Math.min(i + 1, pickableIdx.length - 1)];
      if (typeof next === 'number') setActive(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const i = pickableIdx.indexOf(active);
      const next = pickableIdx[Math.max(i - 1, 0)];
      if (typeof next === 'number') setActive(next);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = flat[active];
      if (row) pick(row);
    }
  }

  if (!open) return null;

  const trimmed = query.trim();
  const showEmptyHint = !trimmed && flat.length === 0;
  const showNoMatch = !!trimmed && !loading && !error && flat.length === 0;

  return (
    <div
      className="cmdk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Search Spotlight"
      onMouseDown={(e) => {
        // Backdrop click closes — don't trap inside-panel clicks.
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="cmdk-panel">
        <div className="cmdk-inputrow">
          <span className="cmdk-icon" aria-hidden="true">/</span>
          <input
            ref={inputRef}
            type="search"
            className="cmdk-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDownInput}
            placeholder="Search projects, tours, content, members, keys, sites, threads…"
            autoComplete="off"
            spellCheck="false"
            aria-controls="cmdk-list"
            aria-autocomplete="list"
          />
          <kbd className="cmdk-esc" aria-hidden="true">Esc</kbd>
        </div>
        <div
          className="cmdk-list"
          id="cmdk-list"
          role="listbox"
          ref={listRef}
        >
          {loading && (
            <div className="cmdk-meta">Searching…</div>
          )}
          {error && (
            <div className="cmdk-meta cmdk-meta--error">{error}</div>
          )}
          {showEmptyHint && (
            <div className="cmdk-meta">
              Start typing to search across this tenant, or press Esc to close.
            </div>
          )}
          {showNoMatch && (
            <div className="cmdk-meta">No matches for &ldquo;{trimmed}&rdquo;.</div>
          )}
          {flat.map((row, i) => {
            if (!row.item) {
              return (
                <div key={`heading-${row.kind}-${i}`} className="cmdk-heading">
                  {row.heading}
                </div>
              );
            }
            const isActive = i === active;
            return (
              <button
                type="button"
                key={`${row.kind}:${row.item.id}:${i}`}
                role="option"
                aria-selected={isActive}
                className={`cmdk-row${isActive ? ' cmdk-row--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pick(row); }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="cmdk-row-glyph" aria-hidden="true">{row.glyph}</span>
                <span className="cmdk-row-meta">
                  <span className="cmdk-row-label">{row.item.label}</span>
                  {row.item.sub && (
                    <span className="cmdk-row-sub">{row.item.sub}</span>
                  )}
                </span>
                <span className="cmdk-row-kind" aria-hidden="true">
                  {KIND_LABEL[row.item.kind || row.kind] || row.kind}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <style>{CMDK_CSS}</style>
    </div>
  );
}

const CMDK_CSS = `
  .cmdk-backdrop {
    position: fixed;
    inset: 0;
    z-index: 100;
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12vh 16px 16px;
  }
  .cmdk-panel {
    width: 100%;
    max-width: 640px;
    background: var(--surface-0, #ffffff);
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.30);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 70vh;
  }
  .cmdk-inputrow {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border-1, #e2e8f0);
  }
  .cmdk-icon {
    color: var(--text-3, #64748b);
    font-weight: 700;
    width: 18px;
    text-align: center;
  }
  .cmdk-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-1, #0f172a);
    font-size: 15px;
    padding: 4px 0;
  }
  .cmdk-esc {
    font-size: 11px;
    color: var(--text-3, #64748b);
    background: var(--surface-1, #f8fafc);
    border: 1px solid var(--border-1, #e2e8f0);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: inherit;
  }
  .cmdk-list {
    overflow-y: auto;
    padding: 6px 0 10px;
  }
  .cmdk-meta {
    color: var(--text-3, #64748b);
    padding: 14px 18px;
    font-size: 13px;
  }
  .cmdk-meta--error { color: var(--danger-700, #b91c1c); }
  .cmdk-heading {
    color: var(--text-3, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 11px;
    font-weight: 700;
    padding: 10px 18px 4px;
  }
  .cmdk-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 18px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
    color: var(--text-1, #0f172a);
    font-size: 13px;
  }
  .cmdk-row--active {
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }
  .cmdk-row-glyph {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: var(--surface-1, #f8fafc);
    color: var(--text-3, #64748b);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .cmdk-row--active .cmdk-row-glyph {
    background: var(--brand-100, #e0e7ff);
    color: var(--brand-700, #4338ca);
  }
  .cmdk-row-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }
  .cmdk-row-label {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cmdk-row-sub {
    color: var(--text-3, #64748b);
    font-size: 11.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cmdk-row-kind {
    color: var(--text-3, #64748b);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    flex-shrink: 0;
  }
`;
